import { ref, computed } from 'vue';
import { usePolicy } from 'dashboard/composables/usePolicy';
import { useRouter } from 'vue-router';
import { useUISettings } from 'dashboard/composables/useUISettings';

const DEFAULT_WIDTH = 200;
const MIN_WIDTH = 56;
const COLLAPSED_THRESHOLD = 160;

// Tier 1 collapsed or not. It is kept as the old resizable sidebar's width (`sidebar_width`), so the choice people
// made there carries over: a width below the threshold is the collapsed rail.
export function useSidebarResize() {
  const { uiSettings, updateUISettings } = useUISettings();

  const sidebarWidth = ref(uiSettings.value.sidebar_width || DEFAULT_WIDTH);
  const isCollapsed = computed(() => sidebarWidth.value < COLLAPSED_THRESHOLD);

  const saveWidth = width => {
    sidebarWidth.value = width;
    updateUISettings({ sidebar_width: width });
  };

  return {
    isCollapsed,
    snapToCollapsed: () => saveWidth(MIN_WIDTH),
    snapToExpanded: () => saveWidth(DEFAULT_WIDTH),
  };
}

/**
 * Whether a menu destination may be shown to the current user (route permissions, feature flag, installation
 * type), and the path it resolves to. Same rules the old sidebar applied, without needing a <Sidebar> parent.
 */
export function useNavAccess() {
  const router = useRouter();
  const { shouldShow } = usePolicy();

  const targetMeta = to => {
    if (!to) return {};
    if (to.params?.navigationPath) {
      const target = router
        .getRoutes()
        .find(route => route.name === to.params.navigationPath);
      return target?.meta ?? {};
    }
    return router.resolve(to)?.meta ?? {};
  };

  const isAllowed = to => {
    const meta = targetMeta(to);
    return shouldShow(
      meta.featureFlag || '',
      meta.permissions ?? [],
      meta.installationTypes || []
    );
  };

  const resolvePath = to => (to ? router.resolve(to)?.path || '/' : '/');

  return { isAllowed, resolvePath };
}
